// Make a batch of coupons (F-POS-06, spec/06 §8.5) in two steps — the batch and what each code grants,
// then its rules — over `createCouponBatch`, then hand over the codes. The server generates them and
// returns them once, in the create response (the list never repeats them), so the last step shows the
// codes with Copy and Download (a CSV made in the browser) before the dialog closes.
//
// A coupon grants a wallet bonus today (`grantKind: wallet`); points, passes and vouchers ride in with
// their catalogues. Registered players redeem a code at the counter (Sell, `redeemCoupon`).

import type { CreateCouponBatchRequest, CreatedCouponBatch } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, FormSection, Stepper, TextField, useToast } from '@clavian/ui-web';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useMyVenues } from '../../api/queries';
import { useScopeIds } from '../../shell/scope';
import { Notice } from '../../ui';
import { moneyFrom } from '../money';
import { useServerErrors } from '../server-errors';
import { useCreateCouponBatch } from './queries';
import { ScopeChoice, type ScopeChoiceKind } from './ScopeChoice';
import { endOfDayIso, startOfDayIso } from './terms';

const STEP_FIELDS: readonly (readonly string[])[] = [['name', 'grant', 'quantity'], ['uses', 'perPlayer', 'dates', 'venues', 'form']];

/** The request's fields -> the form's inputs, for a validation failure. */
const COUPON_SERVER_FIELDS: Readonly<Record<string, string>> = {
  batchName: 'name',
  grantAmount: 'grant',
  quantity: 'quantity',
  maxRedemptionsPerCode: 'uses',
  perPlayerLimit: 'perPlayer',
  validFrom: 'dates',
  validUntil: 'dates',
  venueIds: 'venues',
};

export function CouponBatchFlow({ onDone, onGuardChange }: { readonly onDone: () => void; readonly onGuardChange?: (holding: boolean) => void }): ReactNode {
  const { t } = useTranslation('console');
  const serverErrors = useServerErrors();
  const { show } = useToast();
  const ids = useScopeIds();
  const currency = ids.currency ?? 'INR';
  const myVenues = useMyVenues();
  const venues = useMemo(() => (myVenues.data ?? []).filter((venue) => venue.organizationId === ids.organizationId), [myVenues.data, ids.organizationId]);
  const timezone = venues.find((venue) => venue.id === ids.venueId)?.timezone ?? venues[0]?.timezone ?? 'Asia/Kolkata';
  const create = useCreateCouponBatch(ids.organizationId);

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [grant, setGrant] = useState('');
  const [quantity, setQuantity] = useState('50');
  const [uses, setUses] = useState('1');
  const [perPlayer, setPerPlayer] = useState('1');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  // Where its codes redeem (#709): made from a venue, the batch starts as that venue's only — as it always has.
  const [scopeKind, setScopeKind] = useState<ScopeChoiceKind>(ids.venueId === undefined ? 'organization' : 'venue');
  const [scopeVenueId, setScopeVenueId] = useState<string>(ids.venueId ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<CreatedCouponBatch | null>(null);

  // Once the codes are on screen they cannot be fetched again (the create response carried them once),
  // so tell the dialog to confirm before a stray dismiss throws them away.
  useEffect(() => {
    onGuardChange?.(created !== null);
  }, [created, onGuardChange]);

  const whole = (text: string, min: number, max: number): number | null => {
    const value = Number(text);
    return text.trim() !== '' && Number.isInteger(value) && value >= min && value <= max ? value : null;
  };

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (name.trim() === '') next['name'] = t('pricing.form.required');
    const money = moneyFrom(grant, currency);
    if (money === null || money.amount <= 0) next['grant'] = t('pricing.form.amountInvalid');
    if (whole(quantity, 1, 2000) === null) next['quantity'] = t('pricing.couponFlow.quantityRange');
    if (whole(uses, 1, 100_000) === null) next['uses'] = t('pricing.couponFlow.usesRange');
    if (whole(perPlayer, 1, 100) === null) next['perPlayer'] = t('pricing.couponFlow.perPlayerRange');
    if (scopeKind === 'venue' && scopeVenueId === '') next['venues'] = t('pricing.scopeChoice.venueRequired');
    if (validFrom !== '' && validUntil !== '' && validUntil < validFrom) next['dates'] = t('pricing.form.datesOrder');
    return next;
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const all = validate();
    if (step === 0) {
      const mine = Object.fromEntries(Object.entries(all).filter(([key]) => STEP_FIELDS[0]?.includes(key)));
      setErrors(mine);
      if (Object.keys(mine).length === 0) setStep(1);
      return;
    }
    setErrors(all);
    if (Object.keys(all).length > 0) {
      if (STEP_FIELDS[0]?.some((field) => field in all)) setStep(0);
      return;
    }
    const grantAmount = moneyFrom(grant, currency);
    const body: CreateCouponBatchRequest = {
      batchName: name.trim(),
      grantKind: 'wallet',
      ...(grantAmount === null ? {} : { grantAmount }),
      quantity: whole(quantity, 1, 2000) ?? 1,
      maxRedemptionsPerCode: whole(uses, 1, 100_000) ?? 1,
      perPlayerLimit: whole(perPlayer, 1, 100) ?? 1,
      ...(validFrom === '' ? {} : { validFrom: startOfDayIso(validFrom, timezone) }),
      ...(validUntil === '' ? {} : { validUntil: endOfDayIso(validUntil, timezone) }),
      scope: scopeKind === 'venue' ? 'venues' : 'organization',
      venueIds: scopeKind === 'venue' ? [scopeVenueId] : [],
    };
    create.mutate(body, {
      onSuccess: (batch) => {
        show({ tone: 'ok', title: t('pricing.couponFlow.created', { count: batch.codes.length, name: batch.batchName }) });
        setCreated(batch);
        setStep(2);
      },
      onError: (failure) => {
        const refused = serverErrors(failure, t('pricing.couponFlow.refused'), COUPON_SERVER_FIELDS);
        setErrors(refused);
        const first = STEP_FIELDS.findIndex((fields) => fields.some((field) => field in refused));
        if (first >= 0) setStep(first);
      },
    });
  }

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (created === null && index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'batch', label: t('pricing.couponFlow.stepBatch') },
          { id: 'rules', label: t('pricing.couponFlow.stepRules') },
          { id: 'codes', label: t('pricing.couponFlow.stepCodes') },
        ]}
      />
      {created !== null ? (
        <CreatedCodes batch={created} onDone={onDone} />
      ) : (
        <form className="console-form" onSubmit={submit} noValidate>
          {errors['form'] !== undefined ? (
            <Notice tone="danger" role="alert">
              {errors['form']}
            </Notice>
          ) : null}
          {step === 0 ? (
            <>
              <TextField label={t('pricing.couponFlow.name')} description={t('pricing.couponFlow.nameHint')} required value={name} onChange={(e) => setName(e.target.value)} error={errors['name']} />
              <TextField label={t('pricing.couponFlow.grant', { currency })} description={t('pricing.couponFlow.grantHint')} required numeric inputMode="decimal" value={grant} onChange={(e) => setGrant(e.target.value)} error={errors['grant']} />
              <TextField label={t('pricing.couponFlow.quantity')} description={t('pricing.couponFlow.quantityHint')} required numeric inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} error={errors['quantity']} />
            </>
          ) : (
            <FormSection
              forceOpen={errors['dates'] !== undefined}
              more={
                <>
              <div className="console-form__row">
                <TextField label={t('pricing.form.validFrom')} type="date" showOptional value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                <TextField label={t('pricing.form.validUntil')} type="date" showOptional value={validUntil} onChange={(e) => setValidUntil(e.target.value)} error={errors['dates']} />
              </div>
                </>
              }
            >
              <div className="console-form__row">
                <TextField label={t('pricing.couponFlow.uses')} description={t('pricing.couponFlow.usesHint')} required numeric inputMode="numeric" value={uses} onChange={(e) => setUses(e.target.value)} error={errors['uses']} />
                <TextField label={t('pricing.couponFlow.perPlayer')} description={t('pricing.couponFlow.perPlayerHint')} required numeric inputMode="numeric" value={perPlayer} onChange={(e) => setPerPlayer(e.target.value)} error={errors['perPlayer']} />
              </div>
              <ScopeChoice
                label={t('pricing.couponFlow.where')}
                kind={scopeKind}
                venueId={scopeVenueId}
                venues={venues}
                onKindChange={(kind) => {
                  setScopeKind(kind);
                  if (kind === 'venue' && scopeVenueId === '') setScopeVenueId(ids.venueId ?? venues[0]?.id ?? '');
                }}
                onVenueChange={setScopeVenueId}
                {...(errors['venues'] === undefined ? {} : { error: errors['venues'] })}
              />
            </FormSection>
          )}
          <div className="console-form__actions console-form__actions--end">
            {step === 0 ? (
              <Button type="button" variant="ghost" onClick={onDone}>
                {t('common.cancel')}
              </Button>
            ) : (
              <Button type="button" variant="ghost" onClick={() => setStep(0)}>
                {t('common.back')}
              </Button>
            )}
            <Button type="submit" variant="primary" busy={create.isPending}>
              {step === 0 ? t('common.next') : t('pricing.couponFlow.create')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** The codes of a batch just made: shown once, with Copy all and a CSV download. */
function CreatedCodes({ batch, onDone }: { readonly batch: CreatedCouponBatch; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();

  function copy(): void {
    const text = batch.codes.join('\n');
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (clipboard === undefined) {
      show({ tone: 'danger', title: t('pricing.couponFlow.copyFailed') });
      return;
    }
    clipboard.writeText(text).then(
      () => show({ tone: 'ok', title: t('pricing.couponFlow.copied', { count: batch.codes.length }) }),
      () => show({ tone: 'danger', title: t('pricing.couponFlow.copyFailed') }),
    );
  }

  function download(): void {
    const csv = ['code', ...batch.codes].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${batch.batchName.replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'coupons'}-codes.csv`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="console-form">
      <Notice tone="ok" role="status" title={t('pricing.couponFlow.readyTitle', { count: batch.codes.length })}>
        {t('pricing.couponFlow.readyBody', { amount: batch.grantAmount === null ? '—' : format.money(batch.grantAmount) })}
      </Notice>
      <label className="console-coupon-codes">
        <span className="clav-field__label">{t('pricing.couponFlow.codesLabel', { name: batch.batchName })}</span>
        <textarea className="clav-input console-coupon-codes__text" readOnly rows={Math.min(8, Math.max(3, batch.codes.length))} value={batch.codes.join('\n')} spellCheck={false} />
      </label>
      <p className="console-subtle">{t('pricing.couponFlow.onlyOnce')}</p>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="secondary" onClick={copy}>
          {t('pricing.couponFlow.copy')}
        </Button>
        <Button type="button" variant="secondary" onClick={download}>
          {t('pricing.couponFlow.download')}
        </Button>
        <Button type="button" variant="primary" onClick={onDone}>
          {t('common.done')}
        </Button>
      </div>
    </div>
  );
}
